import axios, { AxiosError, type AxiosInstance, type InternalAxiosRequestConfig } from 'axios';

import { useAuthStore } from '@/stores/auth.store';

const API_URL = import.meta.env.VITE_API_URL ?? '/api/v1';

export const api: AxiosInstance = axios.create({
  baseURL: API_URL,
  timeout: 15_000,
});

// ─── Request: attach access token ────────────────────────────
api.interceptors.request.use((config) => {
  const token = useAuthStore.getState().accessToken;
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

// ─── Response: refresh token rotation on 401 ─────────────────
type RetriableConfig = InternalAxiosRequestConfig & { _retried?: boolean };
let refreshPromise: Promise<string | null> | null = null;

async function performRefresh(): Promise<string | null> {
  const { refreshToken, setSession, clear } = useAuthStore.getState();
  if (!refreshToken) {
    clear();
    return null;
  }
  try {
    const res = await axios.post<{
      data: { accessToken: string; refreshToken: string; expiresIn: number; user: never };
    }>(`${API_URL}/auth/refresh`, { refreshToken });
    const data = res.data.data;
    const user = useAuthStore.getState().user;
    if (user) {
      setSession(
        { accessToken: data.accessToken, refreshToken: data.refreshToken, expiresIn: data.expiresIn },
        user,
      );
    }
    return data.accessToken;
  } catch {
    clear();
    return null;
  }
}

api.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const original = error.config as RetriableConfig | undefined;
    const status = error.response?.status;

    if (status === 401 && original && !original._retried && !original.url?.includes('/auth/')) {
      original._retried = true;
      refreshPromise ??= performRefresh().finally(() => {
        refreshPromise = null;
      });
      const newToken = await refreshPromise;
      if (newToken) {
        original.headers.Authorization = `Bearer ${newToken}`;
        return api.request(original);
      }
    }
    return Promise.reject(error);
  },
);

export type ApiError = {
  code: string;
  message: string | string[];
  details?: unknown;
  request_id?: string;
};

export function extractApiError(err: unknown): ApiError {
  if (axios.isAxiosError(err)) {
    const body = err.response?.data as { error?: ApiError } | undefined;
    if (body?.error) return body.error;
    return { code: 'NETWORK_ERROR', message: err.message };
  }
  if (err instanceof Error) return { code: 'UNKNOWN', message: err.message };
  return { code: 'UNKNOWN', message: 'Unexpected error' };
}

export function formatApiMessage(err: unknown): string {
  const apiErr = extractApiError(err);
  return Array.isArray(apiErr.message) ? apiErr.message.join('; ') : apiErr.message;
}
